
import { Portal } from 'portal-vue'
import type { TranslateResult } from 'vue-i18n'
import { Component, Prop, Vue } from 'vue-property-decorator'

import { TixTimeDuration } from '@/TixTime/Duration'
import { LanguageStrings } from '@/language/types'

@Component({ name: 'ExpiryTimer', components: { Portal } })
export default class extends Vue {
  t: LanguageStrings['expiryTimer']

  @Prop()
  expiresIn: TixTimeDuration

  // Threshold in seconds
  readonly thresholdWarning = 5 * 60
  readonly thresholdError = 2 * 60
  readonly thresholdCritical = 30

  get expiryMinutes(): number {
    return this.expiresIn.minutes()
  }

  get expirySeconds(): number {
    return this.expiresIn.seconds()
  }

  get timeRemainingInSeconds(): number {
    return Math.floor(this.expiresIn.asSeconds())
  }

  // Screen readers announce the section when aria-live changes from 'off' to 'polite'.
  // Set it to 'off' so it doesn't read updates every second.
  get shouldAnnounceExpiryToScreenReader(): 'polite' | 'off' {
    const thresholds = [this.thresholdWarning, this.thresholdError, this.thresholdCritical]

    if (thresholds.includes(this.timeRemainingInSeconds)) {
      return 'polite'
    } else {
      return 'off'
    }
  }

  get formattedTime(): string | null {
    const expiresIn = this.expiresIn
    if (expiresIn.asSeconds() > 0) {
      return expiresIn.format('m:ss')
    } else {
      return null
    }
  }

  get timerStatus(): 'info' | 'warning' | 'error' {
    if (this.timeRemainingInSeconds > this.thresholdWarning) {
      return 'info'
    } else if (this.timeRemainingInSeconds > this.thresholdError) {
      return 'warning'
    } else {
      return 'error'
    }
  }

  get screenReaderTimer(): TranslateResult {
    return this.$t('expiryTimer.screenReaderExpiresIn', {
      minutes: this.expiryMinutes,
      seconds: this.expirySeconds,
    })
  }
}
